import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { notificationSettingsApi } from '../api/client';
import { isReminderUnset } from '../utils/reminderSetup';

/** HH:MM:SS や HH:MM から input time 用 HH:MM を取得 */
function toTimeValue(v: string | null | undefined) {
  if (!v) return '';
  const part = v.split(':').slice(0, 2).join(':');
  return part.length >= 5 ? part : v;
}

/** EMP-S06 通知設定（API 連携） */
export default function SettingsNotifications() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [checkinEnabled, setCheckinEnabled] = useState(true);
  const [feedbackEnabled, setFeedbackEnabled] = useState(true);
  const [messageEnabled, setMessageEnabled] = useState(true);
  const [reminderTime, setReminderTime] = useState('09:00');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /**
   * サーバ上にリマインド時刻が無い状態（課題 #78）。
   * 画面は既定値 09:00 を表示してしまうため、保存前であることを明示しないと
   * 「設定済み」と誤解されて離脱し、リマインドが永久に届かない。
   */
  const [reminderUnset, setReminderUnset] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    notificationSettingsApi
      .get(token)
      .then((data) => {
        setCheckinEnabled(data.checkin_enabled ?? true);
        setFeedbackEnabled(data.feedback_enabled ?? true);
        setMessageEnabled(data.message_enabled ?? true);
        setReminderTime(toTimeValue(data.reminder_time) || '09:00');
        setReminderUnset(isReminderUnset(data));
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
      })
      .finally(() => setLoading(false));
  }, [getToken, t]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const token = getToken();
    if (!token) return;
    setSaveError(null);
    setJustSaved(false);
    setSaving(true);
    const time = reminderTime ? `${reminderTime}:00` : undefined;
    notificationSettingsApi
      .update(
        {
          checkin_enabled: checkinEnabled,
          feedback_enabled: feedbackEnabled,
          message_enabled: messageEnabled,
          reminder_time: time ?? undefined,
        },
        token
      )
      .then((data) => {
        setReminderTime(toTimeValue(data.reminder_time) || '09:00');
        setReminderUnset(isReminderUnset(data));
        setJustSaved(true);
      })
      .catch((err) => setSaveError(err instanceof Error ? err.message : t('common.error')))
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <div className="page settings-sub-page">
        <div className="dashboard-loading">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="page settings-sub-page">
      <h1>{t('settings.notificationSettings.title')}</h1>
      <p className="settings-intro">{t('settings.notificationSettings.intro')}</p>

      {error && (
        <div className="settings-error" role="alert">
          <p>{error}</p>
        </div>
      )}

      {/*
        課題 #78: サーバ上にリマインド時刻が無い状態。時刻欄には既定値 09:00 が入って
        いるため、明示しないと「設定済み」に見えて保存されないまま離脱される。
      */}
      {reminderUnset && (
        <div className="settings-reminder-unset" role="status" data-testid="reminder-unset-notice">
          <p>{t('settings.notificationSettings.unsetNotice')}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="settings-card settings-form">
        <div className="settings-form-group settings-toggle">
          <label>
            <input type="checkbox" checked={checkinEnabled} onChange={(e) => setCheckinEnabled(e.target.checked)} />
            {t('settings.notificationSettings.checkinReminder')}
          </label>
        </div>
        <div className="settings-form-group settings-toggle">
          <label>
            <input type="checkbox" checked={feedbackEnabled} onChange={(e) => setFeedbackEnabled(e.target.checked)} />
            {t('settings.notificationSettings.feedbackNotification')}
          </label>
        </div>
        <div className="settings-form-group settings-toggle">
          <label>
            <input type="checkbox" checked={messageEnabled} onChange={(e) => setMessageEnabled(e.target.checked)} />
            {t('settings.notificationSettings.messageNews')}
          </label>
        </div>
        <div className="settings-form-group">
          <label htmlFor="settings-notificationSettings-reminderTime">{t('settings.notificationSettings.reminderTime')}</label>
          <input id="settings-notificationSettings-reminderTime"
            type="time"
            value={reminderTime}
            onChange={(e) => {
              setReminderTime(e.target.value);
              setJustSaved(false);
            }}
          />
        </div>
        {saveError && <p className="settings-form-error">{saveError}</p>}
        {justSaved && !saveError && (
          <p className="settings-form-saved" role="status" data-testid="reminder-saved">
            {reminderUnset
              ? t('settings.notificationSettings.savedButNoTime')
              : t('settings.notificationSettings.saved', { time: reminderTime })}
          </p>
        )}
        <button type="submit" className="settings-btn settings-btn-primary" disabled={saving}>
          {saving ? t('common.loading') : t('settings.notificationSettings.save')}
        </button>
      </form>

      <p className="settings-back">
        <Link to="/dashboard/settings">{t('common.back')} → {t('settings.backToSettings')}</Link>
      </p>
    </div>
  );
}
